/**
 * JSON-only fallback editor — for when the structured form doesn't fit
 * what the user wants to do (renaming renderSignature, splicing
 * arbitrary fields, etc.).
 *
 * The textarea parses on blur. If the JSON is invalid, the inputs stay
 * dirty and we surface an inline error rather than silently dropping
 * the edit.
 */

import { useEffect, useState } from "react";

import type { Slide, SlideUpdatePayload } from "../types";

const PUTTABLE_FIELDS: Array<keyof SlideUpdatePayload> = [
  "section",
  "slug",
  "titleHtml",
  "meta",
  "bodyHtml",
  "renderPrefix",
  "renderSignature",
];

interface JsonFallbackFormProps {
  draft: Slide;
  setField<K extends keyof SlideUpdatePayload>(
    key: K,
    value: SlideUpdatePayload[K],
  ): void;
  flush(): void;
}

export function JsonFallbackForm({
  draft,
  setField,
  flush,
}: JsonFallbackFormProps) {
  const [text, setText] = useState(() => stringifySlide(draft));
  const [parseError, setParseError] = useState<string | null>(null);

  // Reset text when the underlying draft changes externally (e.g. user
  // clicks another slide). Don't clobber while the textarea is dirty
  // mid-edit.
  useEffect(() => {
    setText(stringifySlide(draft));
    setParseError(null);
  }, [draft.slug, draft.section]);

  const onBlur = () => {
    let parsed: Partial<Slide>;
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err));
      return;
    }
    setParseError(null);
    for (const key of PUTTABLE_FIELDS) {
      const next = parsed[key];
      const current = draft[key];
      if (next !== undefined && next !== current) {
        setField(key, next as SlideUpdatePayload[typeof key]);
      }
    }
    flush();
  };

  return (
    <div data-testid="layout-form-json-fallback">
      <p style={hintStyle}>
        Edit the slide JSON directly. Saves on blur. Fields outside the
        allow-list (<code>{PUTTABLE_FIELDS.join(", ")}</code>) are ignored.
      </p>
      <textarea
        value={text}
        rows={20}
        onChange={(e) => {
          setText(e.target.value);
          setParseError(null);
        }}
        onBlur={onBlur}
        style={textareaStyle}
        spellCheck={false}
        data-testid="json-fallback-textarea"
      />
      {parseError && (
        <p style={errorStyle} data-testid="json-fallback-error">
          JSON parse error: {parseError}
        </p>
      )}
    </div>
  );
}

function stringifySlide(slide: Slide): string {
  const slim: Record<string, unknown> = {};
  for (const key of PUTTABLE_FIELDS) {
    slim[key] = slide[key];
  }
  return JSON.stringify(slim, null, 2);
}

const hintStyle: React.CSSProperties = {
  margin: "0 0 0.5rem",
  fontSize: "0.85em",
  color: "var(--gray-10)",
};

const textareaStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "0.5rem 0.6rem",
  fontFamily: "monospace",
  fontSize: "0.85em",
  border: "1px solid var(--gray-7)",
  borderRadius: 4,
  background: "var(--gray-1)",
  color: "var(--gray-12)",
  resize: "vertical",
};

const errorStyle: React.CSSProperties = {
  margin: "0.5rem 0 0",
  padding: "0.4rem 0.6rem",
  background: "var(--red-3)",
  color: "var(--red-12)",
  border: "1px solid var(--red-7)",
  borderRadius: 4,
  fontSize: "0.85em",
};
